import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import type { SharedRound, SharedRoundResponse } from '../lib/golfRound'

/** What a token came back as — stamped with the token it answers. */
interface SharedRoundResult {
  token: string
  round: SharedRound | null
  dead: boolean
}

export interface SharedRoundBook {
  round: SharedRound | null
  isLoading: boolean
  /** The link opens nothing. See below — this is the ONLY refusal state. */
  dead: boolean
}

/**
 * A round read by its share token — `GET /golf/shared_rounds/:token`.
 *
 * ## Two states, and that is the whole design
 *
 * The endpoint is unauthenticated and the TOKEN IS THE AUTHORITY, exactly like
 * the outing invite this hook is modelled on (`InviteScreen`: *"404 and
 * everything else read the same to the invitee"*). So there is no 401, no 403,
 * no `notFound`-versus-`error` split: **every non-200 is `dead`**.
 *
 * That is not laziness about error handling, it is the point. The server
 * already answers a dead token, a reopened round, a QA round and a token that
 * never existed with ONE uniform 404 envelope so a token cannot be probed; a
 * client that then rendered "that round was taken back" for one and "something
 * went wrong" for another would hand back the distinction the server spent
 * effort withholding. A network fault reading the same is the price, and it is
 * the right one: a stranger holding a link cannot act on the difference anyway.
 *
 * ## It reads NO SESSION
 *
 * No `/me`, no auth context, no membership gate — a share page is opened by
 * people with no account, and asking the server who is reading is a request
 * nobody made. `sharedRound.test.tsx` pins this by rendering the screen with no
 * `AuthProvider` at all.
 *
 * The token is URL-encoded on the way out: it arrives from the address bar, and
 * the club never trusts a path segment it did not mint.
 */
export function useSharedRound(token: string): SharedRoundBook {
  const [result, setResult] = useState<SharedRoundResult | null>(null)

  useEffect(() => {
    let active = true

    void api
      .get<SharedRoundResponse>(`/golf/shared_rounds/${encodeURIComponent(token)}`)
      .then((payload) => {
        if (active) setResult({ token, round: payload.round, dead: false })
      })
      .catch(() => {
        if (active) setResult({ token, round: null, dead: true })
      })

    return () => {
      active = false
    }
  }, [token])

  // DERIVED, never stored — the same shape as `useCourseGeometry`, and for two
  // reasons. An answer counts only for the token it was asked about, so a page
  // whose token changed reads as loading again rather than showing the previous
  // round under the new link; and there is no `setIsLoading(true)` in the
  // effect body to cascade a render (`react-hooks/set-state-in-effect`).
  const settled = result !== null && result.token === token ? result : null

  return {
    round: settled?.round ?? null,
    isLoading: settled === null,
    dead: settled?.dead ?? false,
  }
}
